import { Integration, IntegrationWithJobInfo } from "@/api/integration"
import { ConnectMode, MailboxProvider } from "@/api/mailbox_invite"
import { is_auth_failure } from "@/settings/sync_health"

function is_mail_integration(integration: Integration): boolean {
  return integration.name === "gmail" || integration.name === "microsoft"
}

function normalize_email(email: string): string {
  return email.trim().toLowerCase()
}

/**
 * The provider a mailbox is already connected under, or null when we have never seen it (or have
 * it under both, where the choice is the user's).
 *
 * A mailbox we already sync is hosted in one place, so the provider is not a question to ask
 * again: Compostic's reconnect of a Google Workspace mailbox went to a Microsoft sign-in because
 * the panel's provider defaulted to Microsoft 365. Mirrors `provider_for_mailbox` in
 * `db/src/mailbox_invite.rs`, which holds the same line on the server.
 */
export function known_mailbox_provider(email: string, integrations: Integration[]): MailboxProvider | null {
  const wanted = normalize_email(email)
  if (!wanted) return null
  const providers = new Set(
    integrations
      .filter(is_mail_integration)
      .filter((i: Integration) => normalize_email(i.username) === wanted)
      .map((i: Integration) => i.name as MailboxProvider)
  )
  if (providers.size !== 1) return null
  return [...providers][0]
}

/** Connected mailboxes whose provider has rejected our stored sign-in — only the brand can fix these. */
export function mailboxes_needing_reconnect(integrations: IntegrationWithJobInfo[]): IntegrationWithJobInfo[] {
  return integrations
    .filter(is_mail_integration)
    .filter((i: IntegrationWithJobInfo) => is_auth_failure(i.last_sync_job?.error))
}

/**
 * How a reconnect should sign in. A Microsoft row reading a shared mailbox (`MicrosoftConfig.mailbox`)
 * was authorized by a delegate, and hinting the shared address would send them to an account that
 * usually cannot sign in at all.
 */
export function reconnect_mode(integration: Integration): ConnectMode {
  const shared_mailbox = integration.name === "microsoft" && typeof integration.data?.mailbox === "string" && integration.data.mailbox !== ""
  return shared_mailbox ? "delegated" : "direct"
}
